import { Stack } from '@mui/material';
import { cloneDeep, get } from 'lodash';
import React, { FC, ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingBackdrop from '../../../../components/ui-component/loading/LoadingBackdrop';
import MuiSnackbar from '../../../../components/ui-component/mui-snackbar/MuiSnackbar';
import MUIToolTip from '../../../../components/ui-component/mui-tooltip/MUIToolTip';
import { PostRecord } from '../../../../lib/api/PostRecord';
import { APIS } from '../../../../lib/constants/apis';
import { ANTRAG_VERBUNDPARTNER } from '../../../../lib/constants/componentsPath';
import {
	ANTRAG_BUTTON_NAME,
	ANTRAGS_STATUS,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	NOCH_NICHT_MOEGLICH_TEXT,
	VN_ERSTELLUNG_NOCH_NICHT_MOEGLICH_TEXT,
} from '../../../../lib/constants/constant';
import { PAGES_URLS } from '../../../../lib/constants/routes';
import { enumT } from '../../../../utils/helper';
import { AntragButton, AntragsButtonWrapper, getMonatListe } from '../FoerderungUebersicht';

// Import schema
import Schema from '../../../../lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from '../../../../lib/paths/kostenposition_FULL_Paths.json';
import VerwendungsnachweisSchema from '../../../../lib/paths/verwendungsnachweis_FULL_Paths.json';
const P = Schema.attributes;
const VN_P = VerwendungsnachweisSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const EmobInvestFoerderDashboard: FC<{ application: any }> = ({ application }) => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [wsError, setWsError] = useState(false);

	const checkJahrCondition = false; // Set to true or false as needed
	const currentYear = new Date().getFullYear();

	const antrag = cloneDeep(application);

	const projektDauer = get(antrag, P.Allgemein_Projektdauerin_Jahren.path, 0);
	const projektJahrStart = get(antrag, P.Allgemein_Projektstart_Jahr.path);

	const verwendungsnachweis = get(antrag, P.Foerderantrag_Verwendungsnachweiss.path, []);

	const handleCreateVerwendungsnachweis = async (projektJahr: number) => {
		const allKostenPostion = get(antrag, P.Foerderantrag_Kostenpositions.path);

		const bisherigeVerwendungsnachweise = get(antrag, P.Foerderantrag_Verwendungsnachweiss.path, []).filter((ele: any) => {
			return get(ele, VN_P.Verwendungsnachweisstatus_Status.path, '') !== ANTRAGS_STATUS.abgelehnt;
		});
		const summeBereitAusgezahlteMitten = bisherigeVerwendungsnachweise.reduce((acc: number, curr: any) => {
			return acc + get(curr, VN_P.Ermittlung_Des_Auszahlungsbetrages_Bereits_Ausgezahlte_Mittel.path, 0);
		}, 0);

		const VN_Data = {
			Allgemein_Haushaltsjahr: projektJahr,
			Verwendungsnachweisstatus_Status: ANTRAGS_STATUS.inBearbeitung,
			Foerderantrag: get(antrag, 'id'),
			Allgemein_Foerderquote_Antragstellerin: get(antrag, P.Orga_Foerderquote_Gesamt.path),
			Bankverbindung_Bank: get(antrag, P.Antragstellende_Organisation_Bankverbindung_Geldinstitut.path),
			Bankverbindung_IBAN: get(antrag, P.Antragstellende_Organisation_Bankverbindung_IBAN.path),
			Bankverbindung_BIC: get(antrag, P.Antragstellende_Organisation_Bankverbindung_BIC.path),
			Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung: get(antrag, P.Bewilligung_Jahresaufteilung.path, []).find(
				(ele: any) => get(ele, 'Projektjahr', '') === projektJahr
			)?.Zuwendung,
			Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr:
				!isNaN(summeBereitAusgezahlteMitten) ? Number(summeBereitAusgezahlteMitten) : 0,
			Block_1: [
				{
					Zelle_1: get(antrag, P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation.path, ''),
					Zelle_2: get(antrag, P.Antragstellende_Organisation_Foerderquote.path, []).find((ele: any) => get(ele, 'Projektjahr') === projektJahr)
						?.Foerderquote_403741,
				},
				...get(antrag, P.Verbundpartner.path, [])
					.filter((ele: any) => get(ele, ANTRAG_VERBUNDPARTNER.Name, '') !== '')
					.map((eachVP: any) => {
						return {
							Zelle_1: get(eachVP, ANTRAG_VERBUNDPARTNER.Name, ''),
							Zelle_2: get(eachVP, ANTRAG_VERBUNDPARTNER.FoerderQuoteBlock, []).find((ele: any) => get(ele, 'Projektjahr') === projektJahr)
								?.Foerderquote_403741,
						};
					}),
			],
			Nachweis_Der_Beklebung: [
				{
					Kommentar: '',
				},
			],
		};
		try {
			let vnRes = await PostRecord(APIS.Verwendungsnachweis, { data: VN_Data });
			if (vnRes) {
				for (let i = 0; i < allKostenPostion.length; i++) {
					const eachKP = allKostenPostion[i];
					// Es wird hier nur die Kostenpositionen mit der Position "Ausgabe" berücksichtigt
					// Für die Einnahmen Positionen wird nur ein einziges Nachweis-Postion erstellt
					if (get(eachKP, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe) {
						const nachweisData = {
							Allgemein_Nr: i + 1,
							Allgemein_Kostentraeger: get(eachKP, KP_P.Allgemein_Kostentraeger.path),
							Allgemein_Unternehmen_Institution: get(eachKP, `${KP_P.Unternehmen.path}.id`),
							Verwendungsnachweis: get(vnRes, 'data.id'),
							Allgemein_Kostenposition: get(eachKP, 'id'),
							Allgemein_Kostenart: get(eachKP, KP_P.Allgemein_Kostenposition.path),
							Allgemein_Bezeichnung: get(eachKP, KP_P.Allgemein_Bezeichnung.path),
							Kostennachweise_Nachweise: [{ Kommentar: '' }],
							Stundennachweis_Geleistete_Stunden: getMonatListe(eachKP),
							Allgemein_Standort_Group_ID: get(eachKP, KP_P.Allgemein_Standort_Group_ID.path, ''),
						};
						await PostRecord(APIS.Nachweisposition, { data: nachweisData });
					}
				}
				// Suchen - Ein Eigenmittel Kostenposition, damit wir einen ID für die Nachweis haben.
				const EigenmittelKostenpostion = allKostenPostion.find(
					(ele: any) => get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme
				);
				// Hier werden nur ein einziges Eigenmittel Nachweis-Position erstellt
				const EigenmittelNachweisData = {
					Allgemein_Nr: allKostenPostion.length + 1,
					Allgemein_Kostentraeger: KOSTENTRAEGER.Antragsteller,
					Allgemein_Unternehmen_Institution: get(antrag, `${P.Antragstellende_Organisation.path}.id`),
					Verwendungsnachweis: get(vnRes, 'data.id'),
					Allgemein_Kostenposition: get(EigenmittelKostenpostion, 'id', null),
					Allgemein_Kostenart: KOSTEN_POSITION.Einnahme,
					Allgemein_Bezeichnung: enumT(KOSTEN_ARTEN.Eigenmittel),
					Kostennachweise_Nachweise: [{ Kommentar: '' }],
					Allgemein_Standort_Group_ID: null,
				};
				await PostRecord(APIS.Nachweisposition, { data: EigenmittelNachweisData });
				navigate(`${PAGES_URLS.OnlineAntrag}`, { state: { id: get(vnRes, 'data.id'), api: APIS.Verwendungsnachweis } });
			}
		} catch (err: any) {
			console.error('ERROR: Creating Verwendungsnachweis ', err);
		} finally {
			setLoading(false);
		}
	};

	const RenderVerwendungsnachweisButtons: FC<{}> = () => {
		const buttons: ReactElement[] = [];

		// Group Mittelabruf by Haushaltsjahr (project year)
		const groupedVerwendungsnachweisNachProjektJahr = verwendungsnachweis.reduce((acc: any, item: any) => {
			const year = get(item, VN_P.Allgemein_Haushaltsjahr.path);
			if (!acc[year]) {
				acc[year] = [];
			}
			acc[year].push(item);
			return acc;
		}, {});

		// Loop through each year of the project duration
		for (let i = 0; i < projektDauer; i++) {
			const currentProjektJahr = projektJahrStart + i; // Determine current year

			// Check if there's any Mittelabruf for the current year
			const verwendungsnachweisForYear = groupedVerwendungsnachweisNachProjektJahr[currentProjektJahr];

			// Determine whether the button to create Mittelabruf for this year should be enabled
			const isYearValidForCreation = checkJahrCondition ? currentProjektJahr === currentYear : true;

			// If there are Mittelabruf(s) for the year, show them
			if (verwendungsnachweisForYear && verwendungsnachweisForYear.length > 0) {
				// Checking if the last mittelabruf has the status "freigegeben"
				const isLastNachweisFrei =
					(
						get(verwendungsnachweisForYear[verwendungsnachweisForYear.length - 1], VN_P.Verwendungsnachweisstatus_Status.path, '') ===
							ANTRAGS_STATUS.freigegeben ||
						get(verwendungsnachweisForYear[verwendungsnachweisForYear.length - 1], VN_P.Verwendungsnachweisstatus_Status.path, '') ===
							ANTRAGS_STATUS.abgelehnt
					) ?
						true
					:	false;
				let isVNErstellenButtonDisabled: boolean = false;
				if (isLastNachweisFrei) {
					if (isVNErstellenButtonDisabled) {
						// if last mittelabruf is "freigegeben" but the current year is not valid then no MA can be created
						isVNErstellenButtonDisabled = true;
					} else {
						isVNErstellenButtonDisabled = false;
					}
				} else {
					// Disable the create button, if last MA for this year does not has the status "freigegeben"
					isVNErstellenButtonDisabled = true;
				}
				buttons.push(
					<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf und Verwendungsnachweis für ' + currentProjektJahr + ': '}>
						{verwendungsnachweisForYear.map((eachVN: any, index: number) => {
							return (
								<AntragButton
									key={`vn-${currentProjektJahr}-${index}`}
									name={`Projektjahr: ${get(eachVN, VN_P.Allgemein_Haushaltsjahr.path)} #${index + 1}`}
									description={enumT(get(eachVN, VN_P.Verwendungsnachweisstatus_Status.path))}
									onClick={() =>
										navigate(PAGES_URLS.OnlineAntrag, {
											state: {
												id: get(eachVN, `id`),
												api: APIS.Verwendungsnachweis,
											},
										})
									}
								/>
							);
						})}
						{/* {get(application, P.Antragstatus_Antragstatus.path, '') === ANTRAGS_STATUS.inFoerderung && (
							<MUIToolTip title={isLastNachweisFrei ? '' : LETZTE_MA_VN_ERST_FREI_TEXT}>
								<AntragButton
									name='Mittelabruf und Verwendungsnachweis erstellen'
									description=''
									onClick={() => handleCreateVerwendungsnachweis(currentProjektJahr)}
									disabled={isVNErstellenButtonDisabled}
									variant={'contained'}
								/>
							</MUIToolTip>
						)} */}
					</AntragsButtonWrapper>
				);
			} else {
				// If there are no Mittelabruf for the year, show the button to create a new one
				if (get(antrag, P.Antragstatus_Antragstatus.path) === ANTRAGS_STATUS.inFoerderung) {
					buttons.push(
						<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf und Verwendungsnachweis für ' + currentProjektJahr + ': '}>
							{/* VN-Erstellung temporär für alle Programme deaktiviert (ursprünglich: disabled={!isYearValidForCreation}) */}
							<MUIToolTip title={VN_ERSTELLUNG_NOCH_NICHT_MOEGLICH_TEXT} showTootip={true}>
								<AntragButton
									name='Mittelabruf und Verwendungsnachweis'
									description='erstellen'
									onClick={() => handleCreateVerwendungsnachweis(currentProjektJahr)}
									disabled={true || !isYearValidForCreation}
									variant={'contained'}
								/>
							</MUIToolTip>
						</AntragsButtonWrapper>
					);
				} else {
					buttons.push(
						<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf und Verwendungsnachweis für ' + currentProjektJahr + ': '}>
							<AntragButton name={NOCH_NICHT_MOEGLICH_TEXT} description='offen' onClick={() => {}} disabled={true} />
						</AntragsButtonWrapper>
					);
				}
			}
		}

		return <>{buttons}</>;
	};

	return (
		<>
			<LoadingBackdrop open={loading} />
			<MuiSnackbar showSnackbar={wsError} closeSnackbar={() => setWsError(false)} alertMessage='Etwas ist schief gelaufen' />
			{/* Antrag und Skizze */}
			<Stack direction={'row'} justifyContent={'space-between'}>
				<AntragsButtonWrapper title='Antrag:'>
					<AntragButton
						name={get(antrag, P.Allgemein_Projekttitel.path) ? get(antrag, P.Allgemein_Projekttitel.path) : ANTRAG_BUTTON_NAME}
						description={enumT(get(antrag, P.Antragstatus_Antragstatus.path))}
						onClick={() =>
							navigate(PAGES_URLS.OnlineAntrag, {
								state: {
									id: get(antrag, 'id'),
									api: APIS.Antrag,
									antragsformular: get(antrag, P.Allgemein_Antragsformular.path),
								},
							})
						}
					/>
				</AntragsButtonWrapper>
			</Stack>

			<RenderVerwendungsnachweisButtons />
		</>
	);
};
export default EmobInvestFoerderDashboard;
